import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, ClipboardCheckIcon, ClockIcon, FileTextIcon, MapPinIcon, PencilIcon, PlaneIcon, SendIcon, UsersIcon } from 'lucide-react';
import { useApp } from '../../contexts/AppContext';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Avatar } from '../../components/ui/Avatar';
import { Modal } from '../../components/ui/Modal';
import { Field, inputClass } from '../../components/ui/Field';
import { EventDocuments } from '../../components/shared/EventDocuments';
import { TravelDataPanel } from '../../components/admin/TravelDataPanel';
import { RoomBoard } from '../../components/admin/RoomBoard';
import { TransportBoard } from '../../components/admin/TransportBoard';
import { eventTitle, eventTypeClass, eventTypeLabel, formatDate, formatRange, fullName, functionLabel, memberTypeLabel, voiceLabel } from '../../utils/labels';
type Zavihek = 'pregled' | 'udelezenci' | 'potovanje' | 'nastanitev' | 'prevoz' | 'gradiva' | 'zapisnik';
export function AdminEventDetail() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const {
    events,
    expectedFor,
    rsvps,
    attendance,
    sendMinutes,
    pushToast,
    members,
    today
  } = useApp();
  const [zavihek, setZavihek] = useState<Zavihek>('pregled');
  const [zapisnikModal, setZapisnikModal] = useState(false);
  const [besedilo, setBesedilo] = useState('');
  const [obsegZapisnika, setObsegZapisnika] = useState<'udelezenci' | 'vsi'>('udelezenci');
  const event = events.find(e => e.id === id);
  if (!event) {
    return <Card className="p-10 text-center" data-id="element-811" data-mp="1yru5d0qeto5">
        <p className="text-sm font-medium text-ink" data-id="element-812" data-mp="1xymm9ty804y">Dogodek ne obstaja.</p>
        <Link to="/admin/dogodki" className="mt-2 inline-block text-[13px] font-medium text-green-600" data-id="element-813" data-mp="3ae1r66prl8p2">
          Nazaj na dogodke
        </Link>
      </Card>;
  }
  const udelezenci = expectedFor(event);
  const potovanje = event.potovanje?.aktivno ? event.potovanje : undefined;
  const nastanitev = potovanje?.nastanitev?.aktivno ? potovanje.nastanitev : undefined;
  const prevozAktiven = !!potovanje?.prevoz?.aktivno;
  const organizacijski = event.type === 'zbor' || event.type === 'sestanek';
  const zapisnik = (event.dokumenti ?? []).find(d => d.kind === 'zapisnik');
  const jePreteklo = (event.datumDo ?? event.datum) < today;
  const zavihki: {
    key: Zavihek;
    label: string;
  }[] = useMemo(() => {
    const list: {
      key: Zavihek;
      label: string;
    }[] = [{
      key: 'pregled',
      label: 'Pregled'
    }, {
      key: 'udelezenci',
      label: 'Udeleženci'
    }];
    if (potovanje) list.push({
      key: 'potovanje',
      label: 'Potovalni podatki'
    });
    if (nastanitev) list.push({
      key: 'nastanitev',
      label: 'Nastanitev'
    });
    if (prevozAktiven) list.push({
      key: 'prevoz',
      label: 'Prevoz'
    });
    if (organizacijski || potovanje || (event.dokumenti ?? []).length > 0) {
      list.push({
        key: 'gradiva',
        label: 'Gradiva'
      });
    }
    if (event.type === 'zbor') list.push({
      key: 'zapisnik',
      label: 'Zapisnik'
    });
    return list;
  }, [potovanje, nastanitev, prevozAktiven, organizacijski, event.dokumenti, event.type]);
  const prejemnikiZapisnika = obsegZapisnika === 'udelezenci' ? udelezenci.map(m => m.id) : members.filter(m => m.type !== 'nekdanji').map(m => m.id);
  const odgovori = udelezenci.reduce((acc, m) => {
    const r = rsvps[`${m.id}:${event.id}`];
    if (r?.status === 'pridem') acc.pridem += 1;else if (r?.status === 'ne_morem') acc.neMorem += 1;else acc.brez += 1;
    return acc;
  }, {
    pridem: 0,
    neMorem: 0,
    brez: 0
  });
  return <div className="mx-auto max-w-5xl space-y-4" data-id="element-814" data-mp="1sb6w7s1298fm">
      <button onClick={() => navigate('/admin/dogodki')} className="inline-flex items-center gap-1.5 text-[13px] font-medium text-ink-muted transition-colors duration-150 ease-out hover:text-ink" data-id="element-815" data-mp="3rmhpk6yuwxae">
        <ArrowLeftIcon className="h-4 w-4" data-id="element-816" data-mp="3uo2xti4v9dc9" />
        Nazaj na dogodke
      </button>

      <Card className="p-5" data-id="element-817" data-mp="3il34luwl02j">
        <div className="flex flex-wrap items-start justify-between gap-4" data-id="element-818" data-mp="3gqk5d36zi13">
          <div data-id="element-819" data-mp="3kqyctlmrbnx1">
            <div className="flex flex-wrap items-center gap-1.5" data-id="element-820" data-mp="3kpdh3sophyxl">
              <Badge className={eventTypeClass[event.type]} data-id="element-821" data-mp="3pozxgaztzvam">{eventTypeLabel[event.type]}</Badge>
              {potovanje && <Badge className="bg-purple-50 text-purple-700 border-purple-200" data-id="element-822" data-mp="3bwtop9z0cql7">
                  <PlaneIcon className="h-3 w-3" data-id="element-823" data-mp="3gotjyp44uvbg" />
                  Gostovanje
                </Badge>}
              {event.opomba && <Badge className="bg-canvas text-ink-muted border-line" data-id="element-824" data-mp="31xkq23t76nsi">{event.opomba}</Badge>}
              {!event.steje && <Badge className="bg-slate-100 text-ink-muted border-line" data-id="element-825" data-mp="2syqv0hvim5h2">Ne šteje v prisotnost</Badge>}
              {event.zapisnikPoslan && <Badge className="bg-green-50 text-green-700 border-green-200" dot="bg-green-500" data-id="element-826" data-mp="2x2f26zg8iz1s">
                  Zapisnik poslan {formatDate(event.zapisnikPoslan)}
                </Badge>}
            </div>
            <h1 className="mt-2 text-xl font-semibold text-ink" data-id="element-827" data-mp="2ncq9xbukjuo3">{eventTitle(event)}</h1>
            <p className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px] text-ink-muted" data-id="element-828" data-mp="2idvmtxpkjjy6">
              <span className="flex items-center gap-1.5" data-id="element-829" data-mp="2xawe603ulcw4">
                <ClockIcon className="h-4 w-4" data-id="element-830" data-mp="2x9bl6ex6m20d" />
                {event.datumDo ? formatRange(event.datum, event.datumDo) : `${formatDate(event.datum, {
                weekday: true
              })} · ${event.zacetek}${event.konec ? `–${event.konec}` : ''}`}
              </span>
              <span className="flex items-center gap-1.5" data-id="element-831" data-mp="3g1nl6v0d4r7c">
                <MapPinIcon className="h-4 w-4" data-id="element-832" data-mp="3g1xa0f9o8sbg" />
                {event.lokacija}
              </span>
              <span className="flex items-center gap-1.5" data-id="element-833" data-mp="3b4dvde92adiz">
                <UsersIcon className="h-4 w-4" data-id="element-834" data-mp="28pxx5smgy2ak" />
                {udelezenci.length} pričakovanih
              </span>
            </p>
          </div>

          <div className="flex flex-wrap gap-2" data-id="element-835" data-mp="2conjiaphiu6c">
            <Link to={`/admin/dogodki/${event.id}/uredi`} data-id="element-836" data-mp="2hpkwm8vn0y0z">
              <Button variant="secondary" size="sm" data-id="element-837" data-mp="2ho02ulh5dj46">
                <PencilIcon className="h-4 w-4" data-id="element-838" data-mp="2mhber10tgq2y" />
                Uredi dogodek
              </Button>
            </Link>
            {event.steje && <Link to={`/admin/prisotnost/${event.id}`} data-id="element-839" data-mp="2wepfzte658ao">
                <Button size="sm" data-id="element-840" data-mp="2wd6229fzj9c2">
                  <ClipboardCheckIcon className="h-4 w-4" data-id="element-841" data-mp="30gb1u48okwx0" />
                  Vpiši prisotnost
                </Button>
              </Link>}
          </div>
        </div>
      </Card>

      <div role="tablist" aria-label="Razdelki dogodka" className="flex gap-1.5 overflow-x-auto viva-scroll pb-1" data-id="element-842" data-mp="35d1gqo8r1mgu">
        {zavihki.map(z => <button key={z.key} role="tab" aria-selected={zavihek === z.key} onClick={() => setZavihek(z.key)} className={`shrink-0 rounded-lg border px-3 py-1.5 text-[13px] font-medium transition-colors duration-150 ease-out ${zavihek === z.key ? 'border-green-500 bg-green-500 text-white' : 'border-line bg-white text-ink-muted hover:bg-canvas hover:text-ink'}`} data-id="element-843" data-mp="1b1mpu4405gzi">
            {z.label}
          </button>)}
      </div>

      {zavihek === 'pregled' && <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]" data-id="element-844" data-mp="1hifutn4lhtky">
          <Card className="p-5" data-id="element-845" data-mp="1chs4z4s3risb">
            <h2 className="text-base font-semibold text-ink" data-id="element-846" data-mp="1cgqhu1xtb5u8">Pregled</h2>
            <dl className="mt-3 divide-y divide-line border-t border-line" data-id="element-847" data-mp="18fwvhd00pz3g">
              {[['Za koga velja', event.obcinstvo === 'glasovi' && event.glasovi ? event.glasovi.map(v => voiceLabel[v]).join(', ') : event.obcinstvo === 'funkcije' && event.funkcije ? event.funkcije.map(f => functionLabel[f]).join(', ') : event.obcinstvo === 'izbrani' ? `Izbrani člani (${event.clani?.length ?? 0})` : event.obcinstvo === 'aktivni' ? 'Vsi aktivni člani' : event.obcinstvo === 'sestavljeno' ? [...(event.tipi ?? []).map(t => memberTypeLabel[t]), ...(event.glasovi ?? []).map(v => voiceLabel[v]), ...(event.funkcije ?? []).map(f => functionLabel[f])].join(', ') : 'Vsi člani in gostje'], ['Šteje v prisotnost', event.steje ? 'Da' : 'Ne'], ['Prisotnost vpisana', attendance[event.id] ? 'Da' : 'Ne'], ['Gradiv', String((event.dokumenti ?? []).length)]].map(([k, v]) => <div key={k} className="flex flex-wrap gap-2 py-2.5" data-id="element-848" data-mp="38xuy1z6bhmlm">
                  <dt className="w-48 shrink-0 text-[13px] text-ink-muted" data-id="element-849" data-mp="38viexiotp66z">{k}</dt>
                  <dd className="text-sm text-ink" data-id="element-850" data-mp="3mquattept2ph">{v}</dd>
                </div>)}
            </dl>
            {event.opis && <p className="mt-4 rounded-lg bg-canvas px-4 py-3 text-sm leading-relaxed text-ink" data-id="element-851" data-mp="2tzj0zrf18btx">
                {event.opis}
              </p>}
          </Card>

          <Card className="p-5" data-id="element-852" data-mp="33y8ivk6xhwa6">
            <h2 className="text-base font-semibold text-ink" data-id="element-853" data-mp="33yi8h6o2a1fc">Odzivi udeležencev</h2>
            <div className="mt-3 space-y-2" data-id="element-854" data-mp="2yym6fcyxck2f">
              {[['Potrdilo prihod', odgovori.pridem, 'text-green-600'], ['Napovedana odsotnost', odgovori.neMorem, 'text-rose-600'], ['Brez odgovora', odgovori.brez, 'text-amber-600']].map(([label, value, cls]) => <div key={label as string} className="flex items-center justify-between text-sm" data-id="element-855" data-mp="1n1w0quydisir">
                  <span className="text-ink-muted" data-id="element-856" data-mp="1i5fcm0r6ce5o">{label as string}</span>
                  <span className={`font-semibold ${cls as string}`} data-id="element-857" data-mp="1dcvx2y8xf8y8">{value as number}</span>
                </div>)}
            </div>
            {jePreteklo && <p className="mt-4 rounded-lg bg-canvas px-3 py-2.5 text-[13px] text-ink-muted" data-id="element-858" data-mp="14cpqzxfk5b93">
                Dogodek je že mimo.
              </p>}
          </Card>
        </div>}

      {zavihek === 'udelezenci' && <Card className="overflow-hidden" data-id="element-859" data-mp="ufkzdi2y0nkw">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-5 py-3" data-id="element-860" data-mp="ugcy31ru71fe">
            <h2 className="text-base font-semibold text-ink" data-id="element-861" data-mp="2oskmrxdkem2q">Udeleženci ({udelezenci.length})</h2>
            {event.steje && <Link to={`/admin/prisotnost/${event.id}`} className="text-[13px] font-medium text-green-600 hover:underline" data-id="element-862" data-mp="2jt80peuipj17">
                Vpiši prisotnost
              </Link>}
          </div>
          <ul className="divide-y divide-line" data-id="element-863" data-mp="2w9z9a6r8oil2">
            {udelezenci.map(m => {
          const r = rsvps[`${m.id}:${event.id}`];
          return <li key={m.id} className="flex flex-wrap items-center gap-3 px-5 py-3" data-id="element-864" data-mp="360k7ht303l3g">
                  <Avatar member={m} size="sm" data-id="element-865" data-mp="29cnx55y5pm0o" />
                  <Link to={`/admin/clani/${m.id}`} className="min-w-0 flex-1 truncate text-sm font-medium text-ink hover:text-green-700" data-id="element-866" data-mp="29dz3ndizl7vh">
                    {fullName(m)}
                  </Link>
                  <span className="text-[12px] text-ink-muted" data-id="element-867" data-mp="2nc6ur4tcdeyq">{voiceLabel[m.voice]}</span>
                  <span className="hidden text-[12px] text-ink-muted sm:block" data-id="element-868" data-mp="3tqej67bl33m4">
                    {memberTypeLabel[m.type]}
                  </span>
                  {r?.status === 'pridem' ? <Badge className="bg-green-50 text-green-700 border-green-200" dot="bg-green-500" data-id="element-869" data-mp="391do4wltl7dz">
                      Pride
                    </Badge> : r?.status === 'ne_morem' ? <Badge className="bg-rose-50 text-rose-700 border-rose-200" dot="bg-rose-500" data-id="element-870" data-mp="3iz9xpjz3zu9z">
                      Odsoten/a
                    </Badge> : <Badge className="bg-amber-50 text-amber-800 border-amber-200" data-id="element-871" data-mp="2qb47ihpadd2r">Brez odgovora</Badge>}
                </li>;
        })}
          </ul>
        </Card>}

      {zavihek === 'potovanje' && <TravelDataPanel event={event} udelezenci={udelezenci} data-id="element-872" data-mp="3031oy66w119x" />}
      {zavihek === 'nastanitev' && <RoomBoard event={event} udelezenci={udelezenci} data-id="element-873" data-mp="2v9fhcsfkznkz" />}
      {zavihek === 'prevoz' && <TransportBoard event={event} udelezenci={udelezenci} data-id="element-874" data-mp="27k4r44vuxbl3" />}
      {zavihek === 'gradiva' && <EventDocuments event={event} urejanje data-id="element-875" data-mp="22l9zqejtqepd" />}

      {zavihek === 'zapisnik' && <div className="space-y-4" data-id="element-876" data-mp="i40s0rj9bd5x">
          <Card className="p-5" data-id="element-877" data-mp="i2plijyffrb4">
            <div className="flex flex-wrap items-start justify-between gap-3" data-id="element-878" data-mp="mv0mzr8tfl5s">
              <div data-id="element-879" data-mp="mx3pvbv2h8rx">
                <h2 className="text-base font-semibold text-ink" data-id="element-880" data-mp="864l6bxcr4dm">Zapisnik</h2>
                <p className="mt-0.5 text-sm text-ink-muted" data-id="element-881" data-mp="841kmxykpsun">
                  Po zboru članov naložite zapisnik in ga pošljite udeležencem.
                </p>
              </div>
              <Button onClick={() => {
            setBesedilo(`Pošiljamo vam zapisnik dogodka ${eventTitle(event)} z dne ${formatDate(event.datum)}.\n\nZapisnik je priložen temu obvestilu in shranjen med gradivi dogodka. Morebitne pripombe sporočite tajnici v osmih dneh.`);
            setZapisnikModal(true);
          }} disabled={!zapisnik} data-id="element-882" data-mp="10vbfsztor9tl">
                <SendIcon className="h-4 w-4" data-id="element-883" data-mp="3gnheu8i1mas8" />
                Pošlji zapisnik
              </Button>
            </div>

            {zapisnik ? <div className="mt-4 flex flex-wrap items-center gap-3 rounded-lg border border-line px-4 py-3" data-id="element-884" data-mp="3s2m3c1vg5sff">
                <FileTextIcon className="h-4 w-4 shrink-0 text-ink-muted" data-id="element-885" data-mp="3n2q0i5yhk71g" />
                <a href={zapisnik.link} target="_blank" rel="noreferrer" className="min-w-0 flex-1 truncate text-sm font-medium text-ink hover:text-green-700" data-id="element-886" data-mp="3i46e64fzct8k">
                  {zapisnik.ime}
                </a>
                <span className="text-[12px] text-ink-muted" data-id="element-887" data-mp="9ocujgqcr1na">Dodano {formatDate(zapisnik.dodano)}</span>
                {event.zapisnikPoslan ? <Badge className="bg-green-50 text-green-700 border-green-200" dot="bg-green-500" data-id="element-888" data-mp="5lzrj02zphu7">
                    Poslan {formatDate(event.zapisnikPoslan)}
                  </Badge> : <Badge className="bg-amber-50 text-amber-800 border-amber-200" data-id="element-889" data-mp="2qbffzoeqkugo">Še ni poslan</Badge>}
              </div> : <p className="mt-4 rounded-lg bg-canvas px-4 py-5 text-center text-[13px] text-ink-muted" data-id="element-890" data-mp="19w1k3ztq7op9">
                Zapisnik še ni naložen. Dodajte ga v zavihku Gradiva kot vrsto „Zapisnik“.
              </p>}
          </Card>

          <EventDocuments event={event} urejanje data-id="element-891" data-mp="1jnxk5gfdhow4" />
        </div>}

      <Modal open={zapisnikModal} onClose={() => setZapisnikModal(false)} naslov="Pošlji zapisnik" opis="Zapisnik bo poslan kot obvestilo s priloženim dokumentom." sirina="max-w-xl" noga={<>
            <Button variant="secondary" onClick={() => setZapisnikModal(false)} data-id="element-893" data-mp="1xkaw6a150y37">
              Prekliči
            </Button>
            <Button onClick={() => {
        sendMinutes(event.id, prejemnikiZapisnika, besedilo);
        setZapisnikModal(false);
        pushToast(`Zapisnik je bil poslan ${prejemnikiZapisnika.length} članom.`);
      }} data-id="element-894" data-mp="38mfa2bcilg6">
              <SendIcon className="h-4 w-4" data-id="element-895" data-mp="16j0cwwyjkl3p" />
              Pošlji
            </Button>
          </>} data-id="element-892" data-mp="1oke5k2v6tp5i">
        <div className="space-y-4" data-id="element-896" data-mp="1kjb2lwo47tna">
          <Field label="Prejemniki" htmlFor="obsegZapisnika" data-id="element-897" data-mp="1kkcq50mbi8lw">
            <select id="obsegZapisnika" value={obsegZapisnika} onChange={e => setObsegZapisnika(e.target.value as 'udelezenci' | 'vsi')} className={inputClass} data-id="element-898" data-mp="1fj6v2w56q997">
              <option value="udelezenci" data-id="element-899" data-mp="1ph31xbr3ai1i">Udeleženci dogodka ({udelezenci.length})</option>
              <option value="vsi" data-id="element-900" data-mp="237zjm7jh9ets">Vsi člani in gostje</option>
            </select>
          </Field>
          <Field label="Sporočilo" htmlFor="zapisnikBesedilo" data-id="element-901" data-mp="1z8z4az07kubj">
            <textarea id="zapisnikBesedilo" rows={6} value={besedilo} onChange={e => setBesedilo(e.target.value)} className={inputClass} data-id="element-902" data-mp="2d777honu8rv5" />
          </Field>
          {zapisnik && <p className="flex items-center gap-2 rounded-lg bg-canvas px-3 py-2.5 text-[13px] text-ink-muted" data-id="element-903" data-mp="2rs2x82c6k4pb">
              <FileTextIcon className="h-4 w-4 shrink-0" data-id="element-904" data-mp="2wqwc9z7483rl" />
              Priloga: {zapisnik.ime}
            </p>}
        </div>
      </Modal>
    </div>;
}